import React from "react";
import { Outlet, Route } from "react-router-dom";
import AppLoading from "../components/AppLoading";
import useAuth from "../hooks/useAuth";
import ToastAlertProvider from "@/providers/ToastAlertProvider";
import ThemeProvider from "@/providers/ThemeProvider";
import MainLayout from "@/pages/Shared/MainLayout";
import { AIConfigsProvider } from "@/providers/AIConfigsProvider";
import { AutomationsProvider } from "@/providers/AutomationsProvider";
import { RoleListItemsProvider } from "@/providers/RoleListItemsProvider";
import { RulesProvider } from "@/providers/RulesProvider";
import { SubscriptionsProvider } from "@/providers/SubscriptionsProvider";
import { SelectedTeamProvider } from "@/providers/SelectedTeamProvider";
import LanguageConfigsProvider from "@/pages/Messaging/WebMessenger/providers/LanguageConfigProvider";
import StyleConfigsProvider from "@/pages/Messaging/WebMessenger/providers/StyleConfigProvider";
import { SkillListItemsProvider } from "@/providers/SkillsProvider";
import { TeamsProvider } from "@/providers/TeamsProvider";
import { UsersProvider } from "@/providers/UsersProvider";
import ProfileProvider from "@/providers/ProfileProvider";
import StatusProvider from "@/providers/StatusProvider";
import { SiteProvider } from "@/providers/SiteProvider";
import { WorkflowsProvider } from "@/providers/WorkflowsProvider";
import { ConditionPresetsProvider } from "@/providers/ConditionPresetsProvider";
import { SurveysProvider } from "@/providers/SurveysProvider";
import { SharedSchedulesProvider } from "@/providers/SharedSchedulesProvider";
import { AzureIndexesProvider } from "@/providers/AzureIndexesProvider";
import ChatWindow from "@/pages/Messaging/ChatWindow";
import { ChatWindowsProvider } from "@/providers/ChatWindowsProvider";

export function PrivateRoute(props: any) {
  const { component, ...rest } = props;
  const auth = useAuth();
  const [refreshing, setRefreshing] = React.useState(!auth.isAuthenticated());
  const [authenticated, setAuthenticated] = React.useState(auth.isAuthenticated());

  React.useEffect(() => {
    if (auth.isAuthenticated()) {
      setAuthenticated(true);
      setRefreshing(false);
      return;
    }
    // Try silent refresh for Entra sessions before redirecting
    if (auth.isEntraSession()) {
      auth.entraTokenRefresh().then((ok) => {
        if (ok) {
          setAuthenticated(true);
        } else {
          auth.loginRedirect();
        }
        setRefreshing(false);
      });
    } else {
      auth.loginRedirect();
      // refreshing stays true — AppLoading shown while redirect initiates
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (authenticated) {
    return (
      <ThemeProvider>
      <SiteProvider>
        <TeamsProvider>
          <ProfileProvider>
            <StatusProvider>
              <UsersProvider>
                <SkillListItemsProvider>
                  <SelectedTeamProvider>
                    <RoleListItemsProvider>
                      <ToastAlertProvider>
                        <RulesProvider>
                          <AutomationsProvider>
                            <WorkflowsProvider>
                              <ConditionPresetsProvider>
                              <AIConfigsProvider>
                                <AzureIndexesProvider>
                                  <SurveysProvider>
                                    <SharedSchedulesProvider>
                                    <SubscriptionsProvider>
                                      <ChatWindowsProvider>
                                        <LanguageConfigsProvider>
                                          <StyleConfigsProvider>
                                            <MainLayout>
                                              <Outlet />
                                            </MainLayout>
                                          </StyleConfigsProvider>
                                        </LanguageConfigsProvider>
                                      </ChatWindowsProvider>
                                    </SubscriptionsProvider>
                                    </SharedSchedulesProvider>
                                  </SurveysProvider>
                                </AzureIndexesProvider>
                              </AIConfigsProvider>
                              </ConditionPresetsProvider>
                            </WorkflowsProvider>
                          </AutomationsProvider>
                        </RulesProvider>
                      </ToastAlertProvider>
                    </RoleListItemsProvider>
                  </SelectedTeamProvider>
                </SkillListItemsProvider>
              </UsersProvider>
            </StatusProvider>
          </ProfileProvider>
        </TeamsProvider>
      </SiteProvider>
      </ThemeProvider>
    );
  }

  // Show app loading while refresh is in progress
  return <AppLoading />;
}
